import { MagnifyingGlass } from '@creditornot/cb-icons';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import { useTheme } from 'styled-components';

import { useSharedMenuViewTracking } from 'modules/menu/hooks/telemetryHooks';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';
import { isSharedMenuBootstrapping, isSharedMenuBootstrappingFailure } from 'modules/shared-menu/utils';

import { BootstrapFailure } from './BootstrapFailure';
import { BootstrapInProgress } from './BootstrapInProgress';
import CollectionListErrorBoundary from './CollectionListErrorBoundary';
import { EmptyCollection } from './EmptyCollection';
import ListRowRenderer, { ListRowRendererProps } from './ListRowRenderer';
import {
  Root,
  CollectionContainer,
  HeaderContainer,
  HeaderLeft,
  TitleContainer,
  Title,
  Description,
  HeaderRight,
  ListContainer,
  ListWrapper,
  ContentContainer,
  StyledInput,
} from './styled';
import { useIsNarrowView, useVirtualListHeights, getItemKey, useFocusInputOnCtrlF } from './utils';
import useSharedMenuEditorLists from '../../hooks/useSharedMenuEditorLists';
import EditOptionSidePanel from '../EditOptionSidePanel';
import { NewOfferSidePanel } from '../NewOfferSidePanel';
import { NewOptionSidePanel } from '../NewOptionSidePanel';

type Props = {
  className?: string;
  description?: string;
  onStartMenuBootstrap: () => void;
  title: string;
};

const OptionsCollection = ({ className, description, onStartMenuBootstrap, title }: Props) => {
  const { headerRef, isNarrowView } = useIsNarrowView();
  const { cbDirection } = useTheme();
  const { inputRef } = useFocusInputOnCtrlF();
  const { selection, vlmeConfig } = useSelectionInfoContext();
  const isSharedMenu = !vlmeConfig;
  const isSharedVenue = !!vlmeConfig?.isSharedVenue;
  const menuPermissions = useOfferingMenuPermissionsContext();

  const intl = useIntl();
  useSharedMenuViewTracking('all-options');

  const bootstrapInProgress = useMemo(() => isSharedMenuBootstrapping(selection?.draft), [selection?.draft]);

  const bootstrapFailure = useMemo(() => isSharedMenuBootstrappingFailure(selection?.draft), [selection?.draft]);

  const primaryLanguage = selection.primary_language;

  const {
    menuEditorList,
    activeOption,
    handleOptionCopy,
    setActiveOptionId,
    newItemSidePanelProps,
    newOptionSidePanelProps,
    searchFilter,
    setSearchFilter,
  } = useSharedMenuEditorLists('all-options');

  const { listRef, virtualListHeights } = useVirtualListHeights(menuEditorList);

  if (bootstrapInProgress && isSharedMenu) {
    return <BootstrapInProgress />;
  }

  if (bootstrapFailure && isSharedMenu) {
    return <BootstrapFailure />;
  }

  return (
    <Root className={className}>
      <CollectionContainer>
        <HeaderContainer ref={headerRef}>
          <HeaderLeft dir="auto" lang={primaryLanguage}>
            <TitleContainer>
              <Title title={title}>{title}</Title>
              {!isNarrowView && description && <Description title={description}>{description}</Description>}
            </TitleContainer>
          </HeaderLeft>
          {!activeOption && (
            <HeaderRight>
              <StyledInput
                inputRef={inputRef}
                placeholder={intl.formatMessage({ id: 'menu.searchInCategory' }, { category: title })}
                icon={<MagnifyingGlass />}
                value={searchFilter}
                onChange={event => setSearchFilter(event.currentTarget.value)}
              />
            </HeaderRight>
          )}
        </HeaderContainer>
        <ListContainer>
          <CollectionListErrorBoundary>
            <ListWrapper>
              {menuEditorList.length > 0 ? (
                <AutoSizer disableWidth>
                  {({ height }) => (
                    <VariableSizeList<ListRowRendererProps['data']>
                      ref={listRef}
                      style={{ paddingBlockEnd: 105, direction: cbDirection }}
                      height={height}
                      width="100%"
                      itemData={{
                        itemList: menuEditorList,
                        activeOptionId: activeOption?.id,
                        primaryLanguage,
                        onOptionClick: setActiveOptionId,
                        onCopyOption: handleOptionCopy,
                      }}
                      itemCount={menuEditorList.length}
                      itemSize={index => virtualListHeights[index]}
                      itemKey={(index, data) => getItemKey(index, data.itemList)}
                      overscanCount={6}
                    >
                      {ListRowRenderer}
                    </VariableSizeList>
                  )}
                </AutoSizer>
              ) : (
                <EmptyCollection listType="all-options" onStartMenuBootstrap={onStartMenuBootstrap} />
              )}
            </ListWrapper>
          </CollectionListErrorBoundary>
        </ListContainer>
      </CollectionContainer>
      <ContentContainer>
        {newOptionSidePanelProps.isOpen && !isSharedVenue && menuPermissions.contentEdit && (
          <NewOptionSidePanel {...newOptionSidePanelProps} />
        )}
        {newItemSidePanelProps.isOpen && !isSharedVenue && menuPermissions.contentEdit && (
          <NewOfferSidePanel {...newItemSidePanelProps} />
        )}
        {!!activeOption && <EditOptionSidePanel activeOption={activeOption} onClose={() => setActiveOptionId(null)} />}
      </ContentContainer>
    </Root>
  );
};

export default React.memo(OptionsCollection);
